/* eslint-disable */
import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import {
  message,
  DatePicker,
  InputNumber,
  Alert,
  Spin,
  Card,
  Button,
  Input,
  Switch,
  Row,
  Col,
  Space,
  Typography,
  Tag,
} from "antd";
import {
  CopyOutlined,
  CheckOutlined,
  GiftOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import Header from "../layouts/Header";
import Sidebar from "../layouts/Sidebar";
import { promoCodesApi } from "../../apis";
import dayjs from "dayjs";

const { RangePicker } = DatePicker;
const { Text, Paragraph } = Typography;

const GeneratePromoCodes = () => {
  const history = useHistory();
  const [menu, setMenu] = useState(false);
  const [loading, setLoading] = useState(false);
  const [useCustomPrefix, setUseCustomPrefix] = useState(false);
  const [formData, setFormData] = useState({
    prefix: "",
    count: 10,
    discountPercentage: 15,
    validFrom: "",
    validUntil: "",
    maxUses: 1,
  });
  const [generatedCodes, setGeneratedCodes] = useState([]);
  const [copiedCode, setCopiedCode] = useState(null);

  // Generate default prefix based on current date
  const getDefaultPrefix = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    return `PROMO${year}${month}${day}`;
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleDateRangeChange = (dates) => {
    if (dates && dates.length === 2) {
      // Format for MySQL DATETIME (YYYY-MM-DD HH:MM:SS)
      const startDateTime = dates[0].format("YYYY-MM-DD HH:mm:ss");
      const endDateTime = dates[1].format("YYYY-MM-DD HH:mm:ss");

      setFormData((prev) => ({
        ...prev,
        validFrom: startDateTime,
        validUntil: endDateTime,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        validFrom: "",
        validUntil: "",
      }));
    }
  };

  const handleDiscountChange = (value) => {
    // Allow empty value, default to 0
    if (value === null || value === undefined) {
      handleChange("discountPercentage", 0);
    } else {
      handleChange("discountPercentage", value);
    }
  };

  // Update the validation in handleSubmit
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Get final prefix (custom or default)
    let finalPrefix = formData.prefix;
    if (!useCustomPrefix || !finalPrefix.trim()) {
      finalPrefix = getDefaultPrefix();
    } else {
      finalPrefix = finalPrefix.toUpperCase();
    }

    // Validation
    if (!/^[A-Z0-9]+$/.test(finalPrefix)) {
      return message.error(
        "Prefix must contain only uppercase letters and numbers",
      );
    }
    if (formData.count < 1 || formData.count > 500) {
      return message.error("Count must be between 1 and 500");
    }
    if (formData.discountPercentage < 0 || formData.discountPercentage > 100) {
      return message.error("Discount percentage must be between 0 and 100");
    }
    if (!formData.validFrom || !formData.validUntil) {
      return message.error("Valid date range is required");
    }

    // Parse dates for validation (convert from MySQL format to Date objects)
    const startDate = dayjs(formData.validFrom, "YYYY-MM-DD HH:mm:ss").toDate();
    const endDate = dayjs(formData.validUntil, "YYYY-MM-DD HH:mm:ss").toDate();
    const now = new Date();

    if (startDate > endDate) {
      return message.error("Start date must be before end date");
    }

    if (endDate < now) {
      return message.error("End date cannot be in the past");
    }

    if (formData.maxUses < 1) {
      return message.error("Max uses must be at least 1");
    }

    try {
      setLoading(true);
      const response = await promoCodesApi.bulkCreate({
        prefix: finalPrefix,
        count: formData.count,
        discountPercentage: formData.discountPercentage,
        validFrom: formData.validFrom,
        validUntil: formData.validUntil,
        maxUses: formData.maxUses,
      });

      setGeneratedCodes(response);
      message.success(`Successfully generated ${response.length} promo codes!`);
    } catch (error) {
      const msg = error.response?.data?.message;
      if (Array.isArray(msg)) {
        msg.forEach((m) => message.error(m));
      } else {
        message.error(msg || "Failed to generate promo codes");
      }
      setGeneratedCodes([]);
    } finally {
      setLoading(false);
    }
  };
  const copyToClipboard = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(code);
      message.success(`Copied ${code} to clipboard`);
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (err) {
      message.error("Failed to copy code");
    }
  };

  const copyAllCodes = async () => {
    const allCodes = generatedCodes.map((c) => c.code).join("\n");
    try {
      await navigator.clipboard.writeText(allCodes);
      message.success(`Copied ${generatedCodes.length} codes to clipboard`);
    } catch (err) {
      message.error("Failed to copy codes");
    }
  };

  // Format date for display
  // Format date for display
  const formatDateRange = () => {
    if (formData.validFrom && formData.validUntil) {
      // Parse MySQL datetime format
      const start = dayjs(formData.validFrom, "YYYY-MM-DD HH:mm:ss");
      const end = dayjs(formData.validUntil, "YYYY-MM-DD HH:mm:ss");
      return `${start.format("MMM DD, YYYY HH:mm")} - ${end.format(
        "MMM DD, YYYY HH:mm",
      )}`;
    }
    return "Not selected";
  };

  return (
    <div className={`main-wrapper ${menu ? "slide-nav" : ""}`}>
      <Header onMenuClick={() => setMenu(!menu)} />
      <Sidebar />
      <div className="page-wrapper">
        <div className="content container-fluid">
          <div className="card mb-0">
            <div className="card-body">
              <div className="page-header">
                <div className="content-page-header">
                  <h5>Generate Promo Codes</h5>
                  <button
                    type="button"
                    className="btn btn-primary cancel me-2"
                    onClick={() => history.goBack()}
                    disabled={loading}
                  >
                    Back
                  </button>
                </div>
              </div>

              <Alert
                message="Bulk Promo Code Generation"
                description="Generate multiple unique promo codes at once. Each code will follow the format: PREFIX-XXXXXX where XXXXXX is a random 6-character string."
                type="info"
                showIcon
                icon={<GiftOutlined />}
                className="mb-4"
              />

              <form onSubmit={handleSubmit}>
                <div className="form-group-item">
                  <h5 className="form-title">Promo Code Settings</h5>
                  <div className="row">
                    <div className="col-lg-12 mb-3">
                      <div className="d-flex align-items-center justify-content-between">
                        <label style={{ marginBottom: 0 }}>Custom Prefix</label>
                        <Switch
                          checked={useCustomPrefix}
                          onChange={setUseCustomPrefix}
                          checkedChildren="Custom"
                          unCheckedChildren="Auto"
                        />
                      </div>
                      <small className="text-muted">
                        {useCustomPrefix
                          ? "Enter your own prefix below"
                          : `Using auto-generated prefix: ${getDefaultPrefix()}`}
                      </small>
                    </div>

                    {useCustomPrefix && (
                      <div className="col-lg-6 col-md-6">
                        <div className="input-block mb-3">
                          <label>
                            Custom Prefix <span className="text-danger">*</span>
                          </label>
                          <input
                            className="form-control"
                            placeholder="e.g., SUMMER, WINTER, NEWYEAR"
                            value={formData.prefix}
                            onChange={(e) =>
                              handleChange("prefix", e.target.value)
                            }
                            onInput={(e) => {
                              e.target.value = e.target.value.toUpperCase();
                            }}
                            disabled={loading}
                          />
                          <small className="text-muted">
                            Uppercase letters and numbers only (e.g., SUMMER,
                            SALE2024)
                          </small>
                        </div>
                      </div>
                    )}

                    <div
                      className={`${
                        useCustomPrefix ? "col-lg-6" : "col-lg-12"
                      } col-md-6`}
                    >
                      <div className="input-block mb-3">
                        <label>
                          Number of Codes <span className="text-danger">*</span>
                        </label>
                        <InputNumber
                          className="w-100"
                          min={1}
                          max={500}
                          value={formData.count}
                          onChange={(value) => handleChange("count", value)}
                          disabled={loading}
                        />
                        <small className="text-muted">
                          Maximum 500 codes per batch
                        </small>
                      </div>
                    </div>

                    <div className="col-lg-6 col-md-6">
                      <div className="input-block mb-3">
                        <label>
                          Discount Percentage{" "}
                          <span className="text-danger">*</span>
                        </label>
                        <InputNumber
                          className="w-100"
                          min={0}
                          max={100}
                          value={formData.discountPercentage}
                          onChange={handleDiscountChange}
                          disabled={loading}
                          formatter={(value) => `${value}`}
                          parser={(value) => {
                            const parsed = value.replace("", "");
                            return parsed === "" ? 0 : Number(parsed);
                          }}
                          placeholder="0-100"
                        />
                        <small className="text-muted">
                          0% means no discount (free to enter but no price
                          reduction)
                        </small>
                      </div>
                    </div>

                    <div className="col-lg-6 col-md-6">
                      <div className="input-block mb-3">
                        <label>
                          Max Uses Per Code{" "}
                          <span className="text-danger">*</span>
                        </label>
                        <InputNumber
                          className="w-100"
                          min={1}
                          max={9999}
                          value={formData.maxUses}
                          onChange={(value) => handleChange("maxUses", value)}
                          disabled={loading}
                        />
                        <small className="text-muted">
                          How many times each code can be used (1 = single use)
                        </small>
                      </div>
                    </div>

                    <div className="col-lg-12">
                      <div className="input-block mb-3">
                        <label>
                          Valid Date Range (with Time){" "}
                          <span className="text-danger">*</span>
                        </label>
                        <RangePicker
                          className="w-100"
                          format="YYYY-MM-DD HH:mm"
                          showTime={{
                            format: "HH:mm",
                            minuteStep: 15,
                          }}
                          onChange={handleDateRangeChange}
                          disabled={loading}
                          placeholder={["Start Date & Time", "End Date & Time"]}
                          disabledDate={(current) => {
                            // Disable dates before today
                            return current && current < dayjs().startOf("day");
                          }}
                        />
                        <small className="text-muted">
                          Select date and time range when promo codes will be
                          valid
                        </small>
                      </div>
                    </div>

                    {formData.validFrom && formData.validUntil && (
                      <div className="col-lg-12">
                        <Alert
                          message="Selected Date Range"
                          description={
                            <Space direction="vertical" size={0}>
                              <Text>
                                Start:{" "}
                                {dayjs(formData.validFrom).format(
                                  "MMM DD, YYYY HH:mm",
                                )}
                              </Text>
                              <Text>
                                End:{" "}
                                {dayjs(formData.validUntil).format(
                                  "MMM DD, YYYY HH:mm",
                                )}
                              </Text>
                            </Space>
                          }
                          type="info"
                          showIcon
                          icon={<ClockCircleOutlined />}
                          className="mb-3"
                        />
                      </div>
                    )}
                  </div>
                </div>

                <div className="form-group-item border-0">
                  <div className="row">
                    <div className="col-lg-12">
                      <div className="text-end">
                        <button
                          type="button"
                          className="btn btn-primary cancel me-2"
                          onClick={() => history.goBack()}
                          disabled={loading}
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className="btn btn-primary"
                          style={{
                            backgroundColor: "#7B4D12",
                            borderColor: "#7B4D12",
                          }}
                          disabled={loading}
                        >
                          {loading ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2" />
                              Generating...
                            </>
                          ) : (
                            "Generate Promo Codes"
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </form>

              {/* Generated Codes Display */}
              {generatedCodes.length > 0 && !loading && (
                <div className="mt-4">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="mb-0">
                      <GiftOutlined
                        style={{ marginRight: 8, color: "#7B4D12" }}
                      />
                      Generated Codes ({generatedCodes.length})
                    </h5>
                    <Button
                      onClick={copyAllCodes}
                      type="primary"
                      size="small"
                      icon={<CopyOutlined />}
                      style={{
                        backgroundColor: "#7B4D12",
                        borderColor: "#7B4D12",
                      }}
                    >
                      Copy All
                    </Button>
                  </div>
                  <Row gutter={[16, 16]}>
                    {generatedCodes.map((code) => (
                      <Col xs={24} sm={12} md={8} lg={6} key={code.id}>
                        <Card
                          size="small"
                          style={{
                            cursor: "pointer",
                            border:
                              copiedCode === code.code
                                ? "2px solid #52c41a"
                                : "1px solid #f0f0f0",
                            transition: "all 0.3s ease",
                            backgroundColor:
                              copiedCode === code.code ? "#f6ffed" : "#fff",
                          }}
                          onClick={() => copyToClipboard(code.code)}
                          hoverable
                          actions={[
                            <Button
                              type="text"
                              icon={
                                copiedCode === code.code ? (
                                  <CheckOutlined style={{ color: "#52c41a" }} />
                                ) : (
                                  <CopyOutlined />
                                )
                              }
                              size="small"
                            >
                              {copiedCode === code.code ? "Copied!" : "Copy"}
                            </Button>,
                          ]}
                        >
                          <Card.Meta
                            title={
                              <code
                                style={{
                                  fontSize: 14,
                                  fontWeight: "bold",
                                  wordBreak: "break-all",
                                }}
                              >
                                {code.code}
                              </code>
                            }
                            description={
                              <div>
                                <div className="small text-muted mt-1">
                                  <Tag color="blue">
                                    {code.discountPercentage}% OFF
                                  </Tag>
                                  <Tag color="orange">
                                    {code.maxUses} use(s)
                                  </Tag>
                                </div>
                                <div className="small text-muted mt-1">
                                  <ClockCircleOutlined
                                    style={{ marginRight: 4 }}
                                  />
                                  Valid until:{" "}
                                  {dayjs(code.validUntil).format(
                                    "MMM DD, YYYY [at] HH:mm",
                                  )}
                                </div>
                                <div className="small text-muted mt-1">
                                  <span style={{ fontSize: 11, color: "#999" }}>
                                    Valid from:{" "}
                                    {dayjs(code.validFrom).format(
                                      "MMM DD, YYYY [at] HH:mm",
                                    )}
                                  </span>
                                </div>
                              </div>
                            }
                          />
                        </Card>
                      </Col>
                    ))}
                  </Row>
                  <Alert
                    message="Click any code to copy it to clipboard"
                    description="You can also use the 'Copy All' button to copy all codes at once"
                    type="success"
                    showIcon
                    icon={<CopyOutlined />}
                    className="mt-3"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GeneratePromoCodes;
